import { notification } from 'antd';

/**
 * Utility to handle and classify API errors with i18n support
 */

export const ErrorTypes = {
    UNAUTHORIZED: 'UNAUTHORIZED',      // 401 - handled automatically by interceptor
    FORBIDDEN: 'FORBIDDEN',            // 403
    NOT_FOUND: 'NOT_FOUND',           // 404
    VALIDATION_ERROR: 'VALIDATION_ERROR', // 422
    SERVER_ERROR: 'SERVER_ERROR',     // 5xx
    NETWORK_ERROR: 'NETWORK_ERROR',   // Network issues
    UNKNOWN: 'UNKNOWN'
};

/**
 * Classify an error based on status code or error type
 */
export const classifyError = (error) => {
    if (!error) return ErrorTypes.UNKNOWN;

    // Handle axios or fetch response errors
    if (error.response?.status) {
        const status = error.response.status;
        if (status === 401) return ErrorTypes.UNAUTHORIZED;
        if (status === 403) return ErrorTypes.FORBIDDEN;
        if (status === 404) return ErrorTypes.NOT_FOUND;
        if (status === 422) return ErrorTypes.VALIDATION_ERROR;
        if (status >= 500) return ErrorTypes.SERVER_ERROR;
    }

    // Handle network errors
    if (error.code === 'ERR_NETWORK' || error.message === 'Failed to fetch') {
        return ErrorTypes.NETWORK_ERROR;
    }

    return ErrorTypes.UNKNOWN;
};

/**
 * Get translation key for error type (for use with i18n)
 * @param {string} errorType - The classified error type
 * @param {string} defaultKey - The default translation key if no specific mapping exists
 * @returns {string} The translation key to use with i18n
 */
export const getErrorTranslationKey = (errorType, defaultKey = 'common.errors.server') => {
    const keyMap = {
        [ErrorTypes.UNAUTHORIZED]: 'common.errors.sessionExpired',
        [ErrorTypes.FORBIDDEN]: 'common.errors.forbidden',
        [ErrorTypes.NOT_FOUND]: 'common.errors.notFound',
        [ErrorTypes.VALIDATION_ERROR]: 'common.errors.validation',
        [ErrorTypes.SERVER_ERROR]: 'common.errors.server',
        [ErrorTypes.NETWORK_ERROR]: 'common.errors.network',
    };

    return keyMap[errorType] || defaultKey;
};

/**
 * Get user-friendly error message based on error type and t function from i18n
 * @param {Error} error - The error object
 * @param {Function} t - The i18n translation function (from useTranslation)
 * @param {string} customMessageKey - Optional custom translation key for the message
 * @returns {string} Localized error message
 */
export const getErrorMessage = (error, t, customMessageKey = null) => {
    const errorType = classifyError(error);

    // If a custom message key is provided, use it
    if (customMessageKey && t) {
        return t(customMessageKey);
    }

    // If t function is provided, use translation keys
    if (t) {
        const translationKey = getErrorTranslationKey(errorType);
        return t(translationKey);
    }

    // Fallback to hardcoded messages if no t function is provided
    switch (errorType) {
        case ErrorTypes.UNAUTHORIZED:
            return 'Your session has expired. Please log in again.';
        case ErrorTypes.FORBIDDEN:
            return 'You do not have permission to perform this action.';
        case ErrorTypes.NOT_FOUND:
            return 'The requested resource was not found.';
        case ErrorTypes.VALIDATION_ERROR:
            return error.response?.data?.message || 'Please check your input and try again.';
        case ErrorTypes.SERVER_ERROR:
            return 'Server error. Please try again later.';
        case ErrorTypes.NETWORK_ERROR:
            return 'Network error. Please check your connection and try again.';
        default:
            return 'An error occurred. Please try again.';
    }
};

/**
 * Show error notification for API errors
 * Note: 401 errors are handled automatically by the auth interceptor
 *
 * @param {Error} error - The error object
 * @param {Object} options - Configuration options
 * @param {string} options.title - The notification title (or translation key if t is provided)
 * @param {string} options.message - The notification message (or translation key if t is provided)
 * @param {Function} options.t - The i18n translation function (optional)
 * @param {number} options.duration - Notification duration in seconds
 */
export const showErrorNotification = (error, options = {}) => {
    // Suppress error notifications in development
    if (import.meta.env.DEV) {
        console.warn('[DEV] Suppressed error notification:', error?.message || error);
        return;
    }

    const errorType = classifyError(error);

    // Skip 401 - it's handled by the auth interceptor
    if (errorType === ErrorTypes.UNAUTHORIZED) {
        return;
    }

    const {
        title = 'common.error',
        message = null,
        messageKey = null,
        t = null,
        duration = 4.5,
        ...notificationOptions
    } = options;

    // Translate title if t function is provided
    let translatedTitle = t ? t(title) : title;

    // Translate message
    let translatedMessage;
    if (messageKey && t) {
        // Use provided translation key
        translatedMessage = t(messageKey);
    } else if (message && t && message.startsWith('common.')) {
        // If message looks like a translation key, translate it
        translatedMessage = t(message);
    } else if (message) {
        // Use message as-is or translate if t is provided
        translatedMessage = t ? t(message) : message;
    } else {
        // Fall back to error-type-based message
        translatedMessage = getErrorMessage(error, t);
    }

    notification.error({
        message: translatedTitle,
        description: translatedMessage,
        duration,
        ...notificationOptions
    });
};

/**
 * Helper to extract validation errors from error response
 */
export const getValidationErrors = (error) => {
    if (error.response?.data?.errors) {
        return error.response.data.errors;
    }
    if (error.response?.data?.validationErrors) {
        return error.response.data.validationErrors;
    }
    return null;
};
